/* eslint-disable @typescript-eslint/no-explicit-any */

import { Comment, Fragment, Text, type Slot, type VNode } from 'vue'

/**
 * This is necessary because the addition of fragments and slot changes in Vue 3
 * result in a simple truthy/falsy check of a slot no longer being possible.
 * For example, v-if="$slots.default" can be truthy if the slot contains
 * empty text, comments, fragments, etc.
 *
 * Adapted from solutions provided here:
 * https://github.com/vuejs/core/issues/4733
 */
export function useHasSlotContent() {
  const asArray = <T>(arg: T | T[] | null): T[] =>
    Array.isArray(arg) ? arg : arg !== null ? [arg] : []

  const isVnodeEmpty = (vnode: VNode | VNode[] | undefined | null): boolean => {
    return (
      !vnode ||
      asArray(vnode).every(
        vnode =>
          vnode.type === Comment ||
          (vnode.type === Text && !vnode.children?.length) ||
          (vnode.type === Fragment && !vnode.children?.length),
      )
    )
  }

  const isSlotEmpty = (
    slot: Slot | undefined | null,
    props: any = {},
  ): boolean => isVnodeEmpty(slot?.(props))

  const hasSlotContent = (
    slot: Slot | undefined | null,
    props: any = {},
  ): boolean => !isSlotEmpty(slot, props)

  return {
    hasSlotContent,
  }
}
